/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import { SysqueOptionLineVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-option-line-vm';
import { ConduitSettingsElevationTagComponent } from './tag-options/elevation-tag/conduit-settings-elevation-tag.component';
import { ConduitSettingsItemNumberTagComponent } from './tag-options/item-number-tag/conduit-settings-item-number-tag.component';
import { ConduitSettingsSizeTagComponent } from './tag-options/size-tag/conduit-settings-size-tag.component';
import { ConduitSettingsSpoolTagComponent } from './tag-options/spool-tag/conduit-settings-spool-tag.component';
import { ANNOTATION_SETTINGS_FACADE_TOKEN, IAnnotationSettingsFacade } from "../../../../../../../../data-access/src/lib/domain/facades-models/annotation-settings-facade-interface";
import { Annotation, TagsState } from '../../../../../models/annotation-settings';
import { GlobalConstants } from '../../../../../constants/global-constants';

@Component({
  selector: 'sysque-conduit-settings',
  templateUrl: './conduit-settings.component.html',
  styleUrls: ['./conduit-settings.component.scss'],
  standalone: false
})
export class ConduitSettingsComponent implements OnInit {
  options: SysqueOptionLineVM[] = [];
  tagsState: TagsState;

  constructor(@Inject(ANNOTATION_SETTINGS_FACADE_TOKEN) private annotationSettingsFacade: IAnnotationSettingsFacade) { }

  public ngOnInit(): void {
   //load data for each tag
   this.loadData(this.GetAnnotationData());
  }

  private GetAnnotationData(): Annotation[] {
    this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.SIZE_ANNOTATION_NAME, GlobalConstants.CABLE_TRAY_SIZE_DATA_SELECTION_NAME);
    this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.ELEVATION_ANNOTATION_NAME, GlobalConstants.CABLE_TRAY_ELEVATION_DATA_SELECTION_NAME);
    this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME, GlobalConstants.CABLE_TRAY_ITEM_NUMBER_DATA_SELECTION_NAME);
    this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CABLE_TRAY_TAB_NAME, GlobalConstants.SPOOL_ANNOTATION_NAME, GlobalConstants.CABLE_TRAY_SPOOL_DATA_SELECTION_NAME);
 
    return [
      this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, GlobalConstants.SIZE_ANNOTATION_NAME, GlobalConstants.CONDUIT_SETTINGS_SIZE_DATA_SELECTION_NAME),
      this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, GlobalConstants.ELEVATION_ANNOTATION_NAME, GlobalConstants.CONDUIT_SETTINGS_ELEVATION_DATA_SELECTION_NAME),
      this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, GlobalConstants.ITEM_NUMBER_ANNOTATION_NAME, GlobalConstants.CONDUIT_SETTINGS_ITEM_NUMBER_DATA_SELECTION_NAME),
      this.annotationSettingsFacade.getDataToLoadByTabAndAnnonationName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, GlobalConstants.SPOOL_ANNOTATION_NAME, GlobalConstants.CONDUIT_SETTINGS_SPOOL_DATA_SELECTION_NAME),
    ];
  }

  public loadData(data: Annotation[]): void {
    //get active properties for each tag based on tag name (data can be from xml or from selections)
    this.tagsState = {
      sizeTag: this.annotationSettingsFacade.getActivePropertyByTagName(GlobalConstants.SIZE_TAG_NAME, data) ?? false,
      elevationTag: this.annotationSettingsFacade.getActivePropertyByTagName(GlobalConstants.ELEVATION_TAG_NAME, data) ?? false,
      itemNumberTag: this.annotationSettingsFacade.getActivePropertyByTagName(GlobalConstants.ITEM_NUMBER_TAG_NAME, data) ?? false,
      spoolTag: this.annotationSettingsFacade.getActivePropertyByTagName(GlobalConstants.SPOOL_TAG_NAME, data) ?? false
    };

    this.options = [{
      title: GlobalConstants.SIZE_TAG_NAME,
      content: ConduitSettingsSizeTagComponent,
      expanded: true,
      enabled: this.tagsState.sizeTag
    },
    {
      title: GlobalConstants.ELEVATION_TAG_NAME,
      content: ConduitSettingsElevationTagComponent,
      expanded: false,
      enabled: this.tagsState.elevationTag
    },
    {
      title: GlobalConstants.ITEM_NUMBER_TAG_NAME,
      content: ConduitSettingsItemNumberTagComponent,
      expanded: false,
      enabled: this.tagsState.itemNumberTag
    },
    {
      title: GlobalConstants.SPOOL_TAG_NAME,
      content: ConduitSettingsSpoolTagComponent,
      expanded: false,
      enabled: this.tagsState.spoolTag
    }
    ];

    //save the state of the tags in the local storage
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_TAGS_STATE, this.tagsState);

    this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, this.tagsState, data);
  }

  public onSwitchClicked(item: SysqueOptionLineVM): void {
    const data: Annotation[] = this.GetAnnotationData();
    switch (item.title) {
      case GlobalConstants.SIZE_TAG_NAME:
        this.tagsState.sizeTag = !item.enabled;
        //set Active state for the selected tag in the local storage
        this.annotationSettingsFacade.setActiveValueToLocalStorageByTabNameAndTagName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME,
          item.title, this.tagsState.sizeTag ? GlobalConstants.TRUE : GlobalConstants.FALSE);
        this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, this.tagsState, data);
        break;
      case GlobalConstants.ELEVATION_TAG_NAME:
        this.tagsState.elevationTag = !item.enabled;
        //set Active state for the selected tag in the local storage
        this.annotationSettingsFacade.setActiveValueToLocalStorageByTabNameAndTagName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME,
          item.title, this.tagsState.elevationTag ? GlobalConstants.TRUE : GlobalConstants.FALSE);
        this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, this.tagsState, data);
        break;
      case GlobalConstants.ITEM_NUMBER_TAG_NAME:
        this.tagsState.itemNumberTag = !item.enabled;
        //set Active state for the selected tag in the local storage
        this.annotationSettingsFacade.setActiveValueToLocalStorageByTabNameAndTagName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME,
          item.title, this.tagsState.itemNumberTag ? GlobalConstants.TRUE : GlobalConstants.FALSE);
        this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, this.tagsState, data);
        break;
      case GlobalConstants.SPOOL_TAG_NAME:
        this.tagsState.spoolTag = !item.enabled;
        //set Active state for the selected tag in the local storage
        this.annotationSettingsFacade.setActiveValueToLocalStorageByTabNameAndTagName(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME,
          item.title, this.tagsState.spoolTag ? GlobalConstants.TRUE : GlobalConstants.FALSE);
        this.annotationSettingsFacade.loadImageByTabAndState(GlobalConstants.CONDUIT_SETTINGS_TAB_NAME, this.tagsState, data);
        break;
      default:
        break;
    }

    //update the state of the tags in the local storage
    this.annotationSettingsFacade.updateStorage(GlobalConstants.CONDUIT_SETTINGS_TAGS_STATE, this.tagsState);
  }

}
